import React, { useState, useEffect } from 'react';
import WelcomePage from './components/WelcomePage';
import CustomerScheduling from './components/CustomerScheduling';
import AdminDashboard from './components/admin/AdminDashboard';
import StripePayment from './components/StripePayment';

const App = () => {
  // Determine build type from environment variable
  const BUILD_TYPE = process.env.REACT_APP_BUILD_TYPE || 'customer';
  const IS_ADMIN_BUILD = BUILD_TYPE === 'admin';
  
  // Global state management
  const [currentPage, setCurrentPage] = useState(() => {
    // Check if user is already authenticated
    const isAuthenticated = localStorage.getItem('isAdminAuthenticated') === 'true';
    const loginTime = localStorage.getItem('adminLoginTime');

    // For admin build, check if already logged in
    if (IS_ADMIN_BUILD) {
      if (isAuthenticated && loginTime) {
        const hoursSinceLogin = (Date.now() - parseInt(loginTime)) / (1000 * 60 * 60);
        if (hoursSinceLogin < 24) {
          return 'admin'; // Go directly to admin page
        }
      }
      return 'admin-login';
    }
    return 'schedule';
  });
  
  const [services, setServices] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [adminSettings, setAdminSettings] = useState({
    business_name: 'Alltech LLC',
    admin_email: '',
    business_phone: '',
    business_address: '',
    email_notifications: true,
    sms_notifications: false
  });
  const [isAdmin, setIsAdmin] = useState(() => {
    // Check localStorage for existing admin session
    const isAuthenticated = localStorage.getItem('isAdminAuthenticated') === 'true';
    const loginTime = localStorage.getItem('adminLoginTime');

    // Session expires after 24 hours
    if (isAuthenticated && loginTime) {
      const hoursSinceLogin = (Date.now() - parseInt(loginTime)) / (1000 * 60 * 60);
      if (hoursSinceLogin < 24) {
        return true;
      } else {
        // Clear expired session
        localStorage.removeItem('isAdminAuthenticated');
        localStorage.removeItem('adminLoginTime');
        return false;
      }
    }
    return false;
  });
  const [loading, setLoading] = useState(true);

  // API base URL
  const API_BASE = '/scheduler/api';

  // Check for URL parameters on load
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const adminParam = urlParams.get('admin');
    const directBook = urlParams.get('book');
    const pageParam = urlParams.get('page');
    const resetToken = urlParams.get('token');

    if (IS_ADMIN_BUILD) {
      // Admin build URL handling
      if (pageParam === 'reset-password' && resetToken) {
        setCurrentPage('reset-password');
      } else if (pageParam === 'forgot-password') {
        setCurrentPage('forgot-password');
      }
      // Default admin build starts with admin-login (set in initial state)
    } else {
      // Customer build URL handling
      if (adminParam === 'login') {
        setCurrentPage('admin-login');
      } else if (pageParam === 'reset-password' && resetToken) {
        setCurrentPage('reset-password');
      } else if (directBook === 'now' || directBook === 'true' || directBook === '1') {
        setCurrentPage('schedule');
      }
    }
  }, [IS_ADMIN_BUILD]);

  // Load initial data from database and localStorage
  useEffect(() => {
    console.log('🚀 App.js loaded - BUILD VERSION 2.0 - Payment Fix');
    loadInitialData();
  }, []);

  // Set document title based on build type
  useEffect(() => {
    if (IS_ADMIN_BUILD) {
      document.title = 'Alltech LLC - Admin Dashboard';
    } else {
      document.title = 'Alltech LLC - Scheduling';
    }
  }, [IS_ADMIN_BUILD, currentPage]);

  // CONSOLIDATED loadInitialData function - combining both versions
  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load services from database
      await loadServices();
      
      // Load admin settings from database
      await loadAdminSettings();
      
      // Load appointments from database
      await loadAppointments();

      // Load customers from database
      await loadCustomers();

      // Load blocked dates from database
      await loadBlockedDates();

      // Load coupons from database
      await loadCoupons();

      // Load bundles from database
      await loadBundles();
      
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Database API functions
// Services
  const loadServices = async () => {
    try {
      const response = await fetch(`${API_BASE}/services.php`);
      if (response.ok) {
        const data = await response.json();
        // Accept: [], {data:[]}, {services:[]}, {results:[]}, {items:[]}
        const normalized = Array.isArray(data) ? data
          : (data && Array.isArray(data.data)) ? data.data
          : (data && Array.isArray(data.services)) ? data.services
          : (data && Array.isArray(data.results)) ? data.results
          : (data && Array.isArray(data.items)) ? data.items
          : [];
        setServices(normalized);
      } else {
        setServices([]);
      }
    } catch (error) {
      console.error('Error loading services:', error);
      setServices([]);
    }
  };

  // Customers
  const loadCustomers = async () => {
    try {
      console.log('📥 Loading customers from API...');
      const response = await fetch(`${API_BASE}/customers.php`);
      if (response.ok) {
        const data = await response.json();
        console.log('📥 Raw customer data:', data);
        // Accept: [], {data:[]}, {customers:[]}, {results:[]}, {items:[]}
        const normalized = Array.isArray(data) ? data
          : (data && Array.isArray(data.data)) ? data.data
          : (data && Array.isArray(data.customers)) ? data.customers
          : (data && Array.isArray(data.results)) ? data.results
          : (data && Array.isArray(data.items)) ? data.items
          : [];
        console.log('📥 Normalized customers:', normalized.length, 'items');
        setCustomers(normalized);
      } else {
        console.error('❌ Failed to load customers, HTTP status:', response.status);
        setCustomers([]);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
      setCustomers([]);
    }
  };

  // Coupons
  const loadCoupons = async () => {
    try {
      console.log('📥 Loading coupons from API...');
      const response = await fetch(`${API_BASE}/coupons.php`);
      if (response.ok) {
        const data = await response.json();
        console.log('📥 Raw coupon data:', data);
        // Accept: [], {data:[]}, {coupons:[]}, {results:[]}, {items:[]}
        const normalized = Array.isArray(data) ? data
          : (data && Array.isArray(data.data)) ? data.data
          : (data && Array.isArray(data.coupons)) ? data.coupons
          : (data && Array.isArray(data.results)) ? data.results
          : (data && Array.isArray(data.items)) ? data.items
          : [];
        console.log('📥 Normalized coupons:', normalized.length, 'items');
        setCoupons(normalized);
      } else {
        console.error('❌ Failed to load coupons, HTTP status:', response.status);
        setCoupons([]);
      }
    } catch (error) {
      console.error('💥 Error loading coupons:', error);
      setCoupons([]);
    }
  };

  const loadAdminSettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/admin-settings.php`);
      if (response.ok) {
        const data = await response.json();
        if (data && typeof data === 'object') {
          setAdminSettings(prev => ({ ...prev, ...data }));
        }
      }
    } catch (error) {
      console.error('Error loading admin settings:', error);
    }
  };

  const loadAppointments = async () => {
    try {
      console.log('📅 Loading appointments from API... [BUILD VERSION 2.0]');
      // Add cache-busting timestamp to prevent browser caching
      const timestamp = new Date().getTime();
      const response = await fetch(`${API_BASE}/appointments.php?_=${timestamp}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('📅 Raw appointments data:', data);
        // Normalize possible API shapes: [], {data:[]}, {appointments:[]}, {results:[]}, {items:[]}
        const normalized = Array.isArray(data) ? data
          : (data && Array.isArray(data.data)) ? data.data
          : (data && Array.isArray(data.appointments)) ? data.appointments
          : (data && Array.isArray(data.results)) ? data.results
          : (data && Array.isArray(data.items)) ? data.items
          : [];
        console.log('📅 Normalized appointments:', normalized.length, 'items');
        console.log('📅 Payment statuses:', normalized.map(a => `${a.customer_name}: ${a.payment_status}`).join(', '));
        // Force new array reference to trigger React re-render
        setAppointments([...normalized]);
      }
    } catch (error) {
      console.error('Error loading appointments:', error);
      setAppointments([]);
    }
  };

  const loadBlockedDates = async () => {
    try {
      console.log('🚫 Loading blocked dates...');
      
      const response = await fetch(`${API_BASE}/blocked-dates.php`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📋 Raw blocked dates response:', data);
        
        // FIXED: Extract data from API response properly
        if (data && data.success && Array.isArray(data.data)) {
          console.log('✅ Blocked dates loaded:', data.data.length, 'dates');
          console.log('📝 Sample blocked dates:', data.data.slice(0, 3));
          
          setBlockedDates(data.data);
          
          // DEBUGGING: Make it globally accessible
          window.blockedDatesData = data.data;
          window.blockedDatesCount = data.data.length;
          
          console.log('🔍 Check September dates:', data.data.filter(bd => bd.date.includes('2025-09-')).length);
          
          return data.data;
        } else if (Array.isArray(data)) {
          // Fallback: if response is directly an array
          console.log('✅ Blocked dates loaded (direct array):', data.length, 'dates');
          setBlockedDates(data);
          window.blockedDatesData = data;
          return data;
        } else {
          console.warn('⚠️ Unexpected blocked dates response format:', data);
          setBlockedDates([]);
          return [];
        }
      } else {
        console.error('❌ Failed to load blocked dates - HTTP', response.status);
        setBlockedDates([]);
        return [];
      }
    } catch (error) {
      console.error('💥 Error loading blocked dates:', error);
      setBlockedDates([]);
      return [];
    }
  };

  // Service management functions
  const handleAddService = async (serviceData) => {
    try {
      console.log('📤 Creating service:', serviceData);

      const response = await fetch(`${API_BASE}/services.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(serviceData)
      });

      console.log('📡 Create response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('📡 Create response data:', result);

        if (result.success) {
          await loadServices();
          return { success: true, data: result.data };
        } else {
          console.error('❌ Create failed:', result.error);
          return { success: false, error: result.error || 'Unknown error occurred' };
        }
      } else {
        const errorText = await response.text();
        console.error('❌ HTTP error:', response.status, errorText);

        try {
          const errorJson = JSON.parse(errorText);
          return { success: false, error: errorJson.error || errorText };
        } catch (e) {
          return { success: false, error: errorText || `HTTP ${response.status}` };
        }
      }
    } catch (error) {
      console.error('💥 Exception adding service:', error);
      return { success: false, error: error.message };
    }
  };

  const handleUpdateService = async (serviceData) => {
    try {
      console.log('📤 Updating service:', serviceData);

      const response = await fetch(`${API_BASE}/services.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(serviceData)
      });

      console.log('📡 Update response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('📡 Update response data:', result);

        if (result.success) {
          await loadServices();
          return { success: true, data: result.data };
        } else {
          console.error('❌ Update failed:', result.error);
          return { success: false, error: result.error || 'Unknown error occurred' };
        }
      } else {
        const errorText = await response.text();
        console.error('❌ HTTP error:', response.status, errorText);

        try {
          const errorJson = JSON.parse(errorText);
          return { success: false, error: errorJson.error || errorText };
        } catch (e) {
          return { success: false, error: errorText || `HTTP ${response.status}` };
        }
      }
    } catch (error) {
      console.error('💥 Exception updating service:', error);
      return { success: false, error: error.message };
    }
  };

  const handleDeleteService = async (serviceId) => {
    console.log('🗑️ handleDeleteService called with ID:', serviceId);
    
    try {
      // FIXED: Use query parameter instead of body for DELETE request
      const response = await fetch(`${API_BASE}/services.php?id=${serviceId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
        // NO body for DELETE request - use query parameter instead
      });
      
      console.log('📡 Delete response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('📡 Delete response data:', result);
        
        if (result.success) {
          console.log('✅ Service delete successful:', result.message);
          
          // CRITICAL: Update local state immediately
          setServices(prevServices => {
            const updatedServices = prevServices.filter(service => service.id !== serviceId);
            console.log('🔄 Removed service from state:', serviceId);
            console.log('🔄 New services count:', updatedServices.length);
            return updatedServices;
          });
          
          // Optionally reload from server to ensure consistency
          await loadServices();
          
          return { success: true, message: result.message };
        } else {
          console.error('❌ Delete failed:', result.error);
          return { success: false, error: result.error };
        }
      } else {
        const errorText = await response.text();
        console.error('❌ HTTP error:', response.status, errorText);
        return { success: false, error: `HTTP ${response.status}: ${errorText}` };
      }
    } catch (error) {
      console.error('💥 Exception in handleDeleteService:', error);
      return { success: false, error: error.message };
    }
  };

  // Appointment management functions
  const handleCreateAppointment = async (appointmentData) => {
    try {
      const response = await fetch(`${API_BASE}/appointments.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appointmentData)
      });

      const result = await response.json();
      if (response.ok && result.success) {
        await loadAppointments();
        return result;
      }
      console.error('Appointment creation failed:', result);
      return {
        success: false,
        error: result.error || `HTTP ${response.status}`,
        conflict: response.status === 409
      };
    } catch (error) {
      console.error('Error creating appointment:', error);
      return { success: false, error: error.message };
    }
  };

  const handleUpdateAppointment = async (appointmentData) => {
    try {
      console.log('Updating appointment with data:', appointmentData);
      
      const response = await fetch(`${API_BASE}/appointments.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(appointmentData)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Update response:', result);
        
        if (result.success) {
          await loadAppointments(); // Reload to get updated data
          return { success: true, data: result.data };
        } else {
          console.error('Update failed:', result.error);
          return { success: false, error: result.error };
        }
      } else {
        // A 409 is a real, actionable message (time conflict) - surface it as-is
        // rather than dumping the raw response body at the user.
        const errorText = await response.text();
        console.error('HTTP error:', response.status, errorText);

        let message = `HTTP ${response.status}: ${errorText}`;
        try {
          const parsed = JSON.parse(errorText);
          if (parsed && parsed.error) message = parsed.error;
        } catch (e) {
          // Not JSON - keep the raw text.
        }

        return { success: false, error: message, conflict: response.status === 409 };
      }
    } catch (error) {
      console.error('Error updating appointment:', error);
      return { success: false, error: error.message };
    }
  };
  const handleDeleteAppointment = async (appointmentId) => {
    try {
      // FIXED: Use query parameter for DELETE
      const response = await fetch(`${API_BASE}/appointments.php?id=${appointmentId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          await loadAppointments();
          return { success: true };
        } else {
          return { success: false, error: result.error };
        }
      }
      return { success: false };
    } catch (error) {
      console.error('Error deleting appointment:', error);
      return { success: false };
    }
  };

  // Admin settings
  const saveAdminSettings = async (settings) => {
    try {
      const response = await fetch(`${API_BASE}/admin-settings.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        await loadAdminSettings();
        return { success: true };}
      return { success: false };} catch (error) {
      console.error('Error saving admin settings:', error);
      return { success: false };}
  };

  const handleAdminSettingsUpdate = (newSettings) => {
    setAdminSettings(prev => ({ ...prev, ...newSettings }));
  };

  // Blocked dates functions
  const createBlockedDate = async (dateData) => {
    try {
      console.log('🚫 Creating blocked date:', dateData);
      
      const response = await fetch(`${API_BASE}/blocked-dates.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dateData)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Create blocked date response:', result);
        
        if (result.success) {
          // Reload all blocked dates to ensure state is fresh
          await loadBlockedDates();
          return result;
        } else {
          console.error('❌ Create blocked date failed:', result.error);
          return result;
        }
      } else {
        const errorText = await response.text();
        console.error('❌ HTTP error creating blocked date:', response.status, errorText);
        
        // Try to parse error response
        try {
          const errorJson = JSON.parse(errorText);
          return { success: false, error: errorJson.error || errorText };
        } catch (e) {
          return { success: false, error: errorText };
        }
      }
    } catch (error) {
      console.error('💥 Exception creating blocked date:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteBlockedDate = async (dateId) => {
    try {
      console.log('🗑️ Deleting blocked date ID:', dateId);
      
      // FIXED: Use query parameter for DELETE request instead of body
      const response = await fetch(`${API_BASE}/blocked-dates.php?id=${dateId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
        // No body for DELETE request - use query parameter
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Delete blocked date response:', result);
        
        if (result.success) {
          // Reload all blocked dates to ensure state is fresh
          await loadBlockedDates();
          return result;
        } else {
          console.error('❌ Delete blocked date failed:', result.error);
          return result;
        }
      } else {
        const errorText = await response.text();
        console.error('❌ HTTP error deleting blocked date:', response.status, errorText);
        return { success: false, error: errorText };
      }
    } catch (error) {
      console.error('💥 Exception deleting blocked date:', error);
      return { success: false, error: error.message };
    }
  };

  // Customer functions
  const createCustomer = async (customerData) => {
    try {
      console.log('📤 Creating customer:', customerData);

      const response = await fetch(`${API_BASE}/customers.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerData)
      });

      console.log('📡 Create customer response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('📡 Create customer response data:', result);

        if (result.success) {
          return { success: true, data: result.data };
        } else {
          console.error('❌ Create failed:', result.error);
          return { success: false, error: result.error || 'Unknown error occurred' };
        }
      } else {
        const errorText = await response.text();
        console.error('❌ HTTP error:', response.status, errorText);

        try {
          const errorJson = JSON.parse(errorText);
          return { success: false, error: errorJson.error || errorText };
        } catch (e) {
          return { success: false, error: errorText || `HTTP ${response.status}` };
        }
      }
    } catch (error) {
      console.error('💥 Exception creating customer:', error);
      return { success: false, error: error.message };
    }
  };

  const updateCustomer = async (customerData) => {
    try {
      console.log('📤 Updating customer:', customerData);

      const response = await fetch(`${API_BASE}/customers.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(customerData)
      });

      console.log('📡 Update customer response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('📡 Update customer response data:', result);

        if (result.success) {
          return { success: true, data: result.data };
        } else {
          console.error('❌ Update failed:', result.error);
          return { success: false, error: result.error || 'Unknown error occurred' };
        }
      } else {
        const errorText = await response.text();
        console.error('❌ HTTP error:', response.status, errorText);

        try {
          const errorJson = JSON.parse(errorText);
          return { success: false, error: errorJson.error || errorText };
        } catch (e) {
          return { success: false, error: errorText || `HTTP ${response.status}` };
        }
      }
    } catch (error) {
      console.error('💥 Exception updating customer:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteCustomer = async (customerId) => {
    try {
      console.log('🗑️ Deleting customer ID:', customerId);

      const response = await fetch(`${API_BASE}/customers.php?id=${customerId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      console.log('📡 Delete customer response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('📡 Delete customer response data:', result);

        if (result.success) {
          return { success: true, message: result.message };
        } else {
          console.error('❌ Delete failed:', result.error);
          return { success: false, error: result.error };
        }
      } else {
        const errorText = await response.text();
        console.error('❌ HTTP error:', response.status, errorText);

        try {
          const errorJson = JSON.parse(errorText);
          return { success: false, error: errorJson.error || errorText };
        } catch (e) {
          return { success: false, error: errorText || `HTTP ${response.status}` };
        }
      }
    } catch (error) {
      console.error('💥 Exception deleting customer:', error);
      return { success: false, error: error.message };
    }
  };

  // Coupon functions
  const createCoupon = async (couponData) => {
    try {
      const response = await fetch(`${API_BASE}/coupons.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(couponData)
      });
      
      if (response.ok) {
        await loadCoupons();
        return { success: true };}
      return { success: false };} catch (error) {
      console.error('Error creating coupon:', error);
      return { success: false };}
  };

  const updateCoupon = async (couponData) => {
    try {
      console.log('✏️ App.updateCoupon called with data:', couponData);

      const response = await fetch(`${API_BASE}/coupons.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(couponData)
      });

      console.log('📡 Update response status:', response.status);

      const result = await response.json();
      console.log('📡 Update response data:', result);

      if (response.ok && result.success) {
        console.log('✅ Coupon update successful, reloading coupons...');
        await loadCoupons();
        return { success: true };
      }
      console.error('❌ Update failed:', result.error);
      return { success: false, error: result.error };
    } catch (error) {
      console.error('💥 Exception in updateCoupon:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteCoupon = async (couponId) => {
    try {
      console.log('🗑️ App.deleteCoupon called with ID:', couponId);

      // FIXED: Use query parameter for DELETE
      const response = await fetch(`${API_BASE}/coupons.php?id=${couponId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      console.log('📡 Delete response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('📡 Delete response data:', result);

        if (result.success) {
          console.log('✅ Coupon delete successful, reloading coupons...');
          await loadCoupons();
          console.log('✅ Coupons reloaded, current count:', coupons.length);
          return { success: true };
        } else {
          console.error('❌ Delete failed:', result.error);
          return { success: false, error: result.error };
        }
      }
      console.error('❌ HTTP error:', response.status);
      return { success: false };
    } catch (error) {
      console.error('💥 Exception in deleteCoupon:', error);
      return { success: false };
    }
  };

  // Bundle functions
  const loadBundles = async () => {
    try {
      const response = await fetch(`${API_BASE}/bundles.php`);
      if (response.ok) {
        const data = await response.json();
        const normalized = Array.isArray(data) ? data
          : (data && Array.isArray(data.data)) ? data.data
          : [];
        setBundles(normalized);
      } else {
        setBundles([]);
      }
    } catch (error) {
      console.error('Error loading bundles:', error);
      setBundles([]);
    }
  };

  const createBundle = async (bundleData) => {
    try {
      const response = await fetch(`${API_BASE}/bundles.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bundleData)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          await loadBundles();
          return { success: true, data: result.data };
        }
        return { success: false, error: result.error };
      }
      const errorText = await response.text();
      try {
        const errorJson = JSON.parse(errorText);
        return { success: false, error: errorJson.error || errorText };
      } catch (e) {
        return { success: false, error: errorText };
      }
    } catch (error) {
      console.error('Error creating bundle:', error);
      return { success: false, error: error.message };
    }
  };

  const updateBundle = async (bundleData) => {
    try {
      const response = await fetch(`${API_BASE}/bundles.php`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bundleData)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          await loadBundles();
          return { success: true };
        }
        return { success: false, error: result.error };
      }
      return { success: false };
    } catch (error) {
      console.error('Error updating bundle:', error);
      return { success: false, error: error.message };
    }
  };

  const deleteBundle = async (bundleId) => {
    try {
      const response = await fetch(`${API_BASE}/bundles.php?id=${bundleId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          await loadBundles();
          return { success: true };
        }
        return { success: false, error: result.error };
      }
      return { success: false };
    } catch (error) {
      console.error('Error deleting bundle:', error);
      return { success: false, error: error.message };
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-800">Loading Alltech LLC...</h2>
          <p className="text-gray-600">
            {IS_ADMIN_BUILD 
              ? "Loading admin dashboard..." 
              : "Please wait while we load your services and settings."
            }
          </p>
        </div>
      </div>
    );
  }

  // Shared props for all components
  const sharedProps = {
    currentPage,
    setCurrentPage: handlePageChange,
    services,
    setServices,
    appointments,
    setAppointments,
    customers,
    setCustomers,
    coupons,
    setCoupons,
    blockedDates,
    setBlockedDates,
    adminSettings,
    setAdminSettings: handleAdminSettingsUpdate,
    isAdmin,
    setIsAdmin,
    buildType: BUILD_TYPE,
    isAdminBuild: IS_ADMIN_BUILD,
    // Database API functions
    createService: handleAddService,
    updateService: handleUpdateService,
    deleteService: handleDeleteService,
    createAppointment: handleCreateAppointment,
    updateAppointment: handleUpdateAppointment,
    deleteAppointment: handleDeleteAppointment,
    saveAdminSettings,
    // Blocked dates functions
    createBlockedDate,
    deleteBlockedDate,
    // Re-fetch from the database, for the manual refresh on the calendar
    loadAppointments,
    loadBlockedDates,
    // Coupon functions
    createCoupon,
    updateCoupon,
    deleteCoupon,
    // Bundle functions
    bundles,
    setBundles,
    createBundle,
    updateBundle,
    deleteBundle,
    // Customer functions
    createCustomer: createCustomer,
    updateCustomer: updateCustomer,
    deleteCustomer: deleteCustomer,
    loadCustomers: loadCustomers
  };

  // Force customer functions to not be tree-shaken by webpack
  if (IS_ADMIN_BUILD && false) {
    // This code never runs but prevents tree-shaking
    createCustomer({});
    updateCustomer({});
    deleteCustomer(0);
  }

  // Debug log to verify customer functions are in props
  if (IS_ADMIN_BUILD) {
    console.log('🔍 SharedProps Keys:', Object.keys(sharedProps));
    console.log('🔍 Has createCustomer:', 'createCustomer' in sharedProps, typeof sharedProps.createCustomer);
    console.log('🔍 Customer functions:', {
      createCustomer: typeof createCustomer,
      updateCustomer: typeof updateCustomer,
      deleteCustomer: typeof deleteCustomer
    });
  }

  // Admin build: Only show admin interface
  if (IS_ADMIN_BUILD) {
    return <AdminDashboard {...sharedProps} />;
  }

  // Customer build: Route to appropriate component based on current page
  if (currentPage === 'welcome') {
    return <WelcomePage {...sharedProps} />;
  }

  if (currentPage === 'schedule' || currentPage === 'confirmation') {
    return <CustomerScheduling {...sharedProps} />;
  }

  if (currentPage === 'payment') {
    return (
      <StripePayment
        appointmentData={appointments[appointments.length - 1]} // Last created appointment
        onSuccess={() => setCurrentPage('confirmation')}
        onCancel={() => setCurrentPage('confirmation')}
      />
    );
  }

  if (currentPage === 'admin-login' || currentPage === 'forgot-password' || currentPage === 'reset-password' || (currentPage === 'admin' && isAdmin)) {
    return <AdminDashboard {...sharedProps} />;
  }

  return <WelcomePage {...sharedProps} />;
};

export default App;